import { defineMessages } from 'react-intl';

export const scope = 'app.containers.OfferLetterTemplates';

export default defineMessages({
  // List
  pageTitle: { id: `${scope}.pageTitle`, defaultMessage: 'Offer letter templates' },
  pageDescription: {
    id: `${scope}.pageDescription`,
    defaultMessage:
      'Upload a document to configure variables, conditional blocks, and signature fields.',
  },
  uploadTemplate: { id: `${scope}.uploadTemplate`, defaultMessage: 'Upload template' },
  searchPlaceholder: { id: `${scope}.searchPlaceholder`, defaultMessage: 'Search templates' },
  resultsCount: {
    id: `${scope}.resultsCount`,
    defaultMessage: '{count, plural, one {# template} other {# templates}}',
  },
  noSearchResults: {
    id: `${scope}.noSearchResults`,
    defaultMessage: 'No templates match your search.',
  },
  paginationPrevious: { id: `${scope}.paginationPrevious`, defaultMessage: 'Previous' },
  // "Next" reuses globalMessages.next (shared/global/messages.ts) — see the
  // i18n-shared-strings skill. No local "no previous global equivalent"
  // exists, so paginationPrevious stays here.
  paginationStatus: {
    id: `${scope}.paginationStatus`,
    defaultMessage: 'Page {current} of {total}',
  },
  variablesCount: {
    id: `${scope}.variablesCount`,
    defaultMessage: '{count, plural, one {# variable} other {# variables}}',
  },
  conditionsCount: {
    id: `${scope}.conditionsCount`,
    defaultMessage: '{count, plural, one {# condition} other {# conditions}}',
  },
  emptyTitle: { id: `${scope}.emptyTitle`, defaultMessage: 'No templates yet' },
  emptyBody: {
    id: `${scope}.emptyBody`,
    defaultMessage:
      'Upload an offer letter document to detect its variables and conditional blocks.',
  },
  loadError: {
    id: `${scope}.loadError`,
    defaultMessage: 'We were unable to load offer letter templates.',
  },
  open: { id: `${scope}.open`, defaultMessage: 'Open' },
  templateActions: {
    id: `${scope}.templateActions`,
    defaultMessage: 'template actions',
  },
  // List card meta + delete (AXHE-3755)
  updatedByMeta: {
    id: `${scope}.updatedByMeta`,
    defaultMessage: 'Updated {date} by {name}',
  },
  usageCount: {
    id: `${scope}.usageCount`,
    defaultMessage: '{count, plural, one {# stage} other {# stages}}',
  },
  deleteConfirmTitle: {
    id: `${scope}.deleteConfirmTitle`,
    defaultMessage: 'Delete this template?',
  },
  deleteConfirmBody: {
    id: `${scope}.deleteConfirmBody`,
    defaultMessage: "This can't be undone.",
  },
  deleteConfirmButton: {
    id: `${scope}.deleteConfirmButton`,
    defaultMessage: 'Delete template',
  },
  templateDeleted: {
    id: `${scope}.templateDeleted`,
    defaultMessage: 'Template deleted.',
  },
  deleteError: {
    id: `${scope}.deleteError`,
    defaultMessage: 'We could not delete this template. Please try again.',
  },
  contractTypeFullTime: {
    id: `${scope}.contractTypeFullTime`,
    defaultMessage: 'Full-time',
  },
  contractTypePartTime: {
    id: `${scope}.contractTypePartTime`,
    defaultMessage: 'Part-time',
  },
  contractTypeContract: {
    id: `${scope}.contractTypeContract`,
    defaultMessage: 'Contract',
  },
  contractTypeTemporary: {
    id: `${scope}.contractTypeTemporary`,
    defaultMessage: 'Temporary',
  },
  contractTypeSeasonal: {
    id: `${scope}.contractTypeSeasonal`,
    defaultMessage: 'Seasonal',
  },
  // Upload modal
  uploadModalTitle: { id: `${scope}.uploadModalTitle`, defaultMessage: 'Upload offer letter' },
  dropzonePrompt: { id: `${scope}.dropzonePrompt`, defaultMessage: 'Drag a file here, or {browse}' },
  dropzoneBrowse: { id: `${scope}.dropzoneBrowse`, defaultMessage: 'browse' },
  dropzoneHint: { id: `${scope}.dropzoneHint`, defaultMessage: '.docx up to 10 MB' },
  unsupportedFileType: {
    id: `${scope}.unsupportedFileType`,
    defaultMessage: 'Only .docx files are supported.',
  },
  removeFile: { id: `${scope}.removeFile`, defaultMessage: 'Remove file' },
  templateNameLabel: { id: `${scope}.templateNameLabel`, defaultMessage: 'Template name' },
  templateNamePlaceholder: {
    id: `${scope}.templateNamePlaceholder`,
    defaultMessage: 'e.g. Warehouse Associate US',
  },
  templateNameRequired: {
    id: `${scope}.templateNameRequired`,
    defaultMessage: 'Template name is required',
  },
  documentTitleLabel: { id: `${scope}.documentTitleLabel`, defaultMessage: 'Document title' },
  documentTitlePlaceholder: {
    id: `${scope}.documentTitlePlaceholder`,
    defaultMessage: 'e.g. Your offer letter',
  },
  applicantMessageLabel: {
    id: `${scope}.applicantMessageLabel`,
    defaultMessage: 'Applicant message',
  },
  applicantMessagePlaceholder: {
    id: `${scope}.applicantMessagePlaceholder`,
    defaultMessage: 'Welcome to the team! Please review and sign your offer letter…',
  },
  fileRequired: { id: `${scope}.fileRequired`, defaultMessage: 'Please attach a document' },
  // "Cancel" and "Continue" reuse globalMessages.cancel / globalMessages.continue
  // (shared/global/messages.ts) — see the i18n-shared-strings skill.
  uploadSuccess: {
    id: `${scope}.uploadSuccess`,
    defaultMessage: 'Template uploaded. Detecting variables…',
  },
  // Editor shell
  backToTemplates: { id: `${scope}.backToTemplates`, defaultMessage: 'Close' },
  save: { id: `${scope}.save`, defaultMessage: 'Save' },
  editTemplateName: {
    id: `${scope}.editTemplateName`,
    defaultMessage: 'Edit template name',
  },
  templateSaved: {
    id: `${scope}.templateSaved`,
    defaultMessage: 'Changes saved.',
  },
  editorLoadError: {
    id: `${scope}.editorLoadError`,
    defaultMessage: 'We were unable to load this template.',
  },
  previewUnavailable: {
    id: `${scope}.previewUnavailable`,
    defaultMessage: 'Document preview is unavailable.',
  },
  variablesTab: { id: `${scope}.variablesTab`, defaultMessage: 'Variables' },
  conditionalBlocksTab: {
    id: `${scope}.conditionalBlocksTab`,
    defaultMessage: 'Conditional blocks',
  },
  signaturesTab: { id: `${scope}.signaturesTab`, defaultMessage: 'Signatures' },
  // Variables rail
  variablesTabHint: {
    id: `${scope}.variablesTabHint`,
    defaultMessage: 'Map each detected variable to a Fountain field.',
  },
  noVariables: {
    id: `${scope}.noVariables`,
    defaultMessage: 'No variables were detected in this document.',
  },
  unmappedWarning: { id: `${scope}.unmappedWarning`, defaultMessage: 'Needs mapping' },
  mapToFieldOption: { id: `${scope}.mapToFieldOption`, defaultMessage: 'Map to Fountain field' },
  createNewFieldOption: {
    id: `${scope}.createNewFieldOption`,
    defaultMessage: 'Create a new data field',
  },
  fieldMappingLabel: { id: `${scope}.fieldMappingLabel`, defaultMessage: 'Fountain field' },
  fieldMappingPlaceholder: {
    id: `${scope}.fieldMappingPlaceholder`,
    defaultMessage: 'Select a field…',
  },
  mappingUpdatesPreview: {
    id: `${scope}.mappingUpdatesPreview`,
    defaultMessage: 'Selecting a field updates the variable token in the document preview.',
  },
  fieldSaved: { id: `${scope}.fieldSaved`, defaultMessage: 'Variable updated.' },
  signersSaved: {
    id: `${scope}.signersSaved`,
    defaultMessage: 'Signers updated.',
  },
  // Create new data field — type, currency spell-out, formulas
  dataTypeLabel: { id: `${scope}.dataTypeLabel`, defaultMessage: 'Data type' },
  dataTypeText: { id: `${scope}.dataTypeText`, defaultMessage: 'Text' },
  dataTypeNumber: { id: `${scope}.dataTypeNumber`, defaultMessage: 'Number' },
  dataTypeCurrency: { id: `${scope}.dataTypeCurrency`, defaultMessage: 'Currency' },
  dataTypeDate: { id: `${scope}.dataTypeDate`, defaultMessage: 'Date' },
  dataTypeAddress: { id: `${scope}.dataTypeAddress`, defaultMessage: 'Address' },
  dataTypeBoolean: { id: `${scope}.dataTypeBoolean`, defaultMessage: 'Yes/No' },
  spellOutLabel: {
    id: `${scope}.spellOutLabel`,
    defaultMessage: 'Also include the written-out amount',
  },
  formulasLabel: { id: `${scope}.formulasLabel`, defaultMessage: 'Formulas (optional)' },
  addFormula: { id: `${scope}.addFormula`, defaultMessage: 'Add formula' },
  formulaPlaceholder: {
    id: `${scope}.formulaPlaceholder`,
    defaultMessage: 'e.g. annual_salary / 12',
  },
  removeFormula: { id: `${scope}.removeFormula`, defaultMessage: 'Remove formula' },
  formulaOperatorsHint: {
    id: `${scope}.formulaOperatorsHint`,
    defaultMessage:
      'Reference another data key to derive this value automatically. Operators: + − × ÷',
  },
  // Conditional blocks rail
  conditionalBlocksHint: {
    id: `${scope}.conditionalBlocksHint`,
    defaultMessage: 'Choose when each block appears in the generated letter.',
  },
  noConditionalBlocks: {
    id: `${scope}.noConditionalBlocks`,
    defaultMessage: 'No conditional blocks were detected in this document.',
  },
  conditionsMatchHint: {
    id: `${scope}.conditionsMatchHint`,
    defaultMessage: 'Show this block if ANY of these are true:',
  },
  alwaysShownHint: {
    id: `${scope}.alwaysShownHint`,
    defaultMessage:
      'No conditions yet — this block is always shown. Add a condition to make it optional.',
  },
  conditionDataKeyLabel: { id: `${scope}.conditionDataKeyLabel`, defaultMessage: 'Data key' },
  conditionDataKeyPlaceholder: {
    id: `${scope}.conditionDataKeyPlaceholder`,
    defaultMessage: 'Select a field…',
  },
  conditionOperatorLabel: { id: `${scope}.conditionOperatorLabel`, defaultMessage: 'Operator' },
  conditionValueLabel: { id: `${scope}.conditionValueLabel`, defaultMessage: 'Value' },
  addCondition: { id: `${scope}.addCondition`, defaultMessage: 'Add condition' },
  removeCondition: { id: `${scope}.removeCondition`, defaultMessage: 'Remove condition' },
  saveConditions: { id: `${scope}.saveConditions`, defaultMessage: 'Save conditions' },
  conditionsSaved: { id: `${scope}.conditionsSaved`, defaultMessage: 'Conditions updated.' },
  operatorEquals: { id: `${scope}.operatorEquals`, defaultMessage: 'equals' },
  operatorNotEquals: { id: `${scope}.operatorNotEquals`, defaultMessage: 'does not equal' },
  operatorPresent: { id: `${scope}.operatorPresent`, defaultMessage: 'is present' },
  operatorBlank: { id: `${scope}.operatorBlank`, defaultMessage: 'is blank' },
  operatorGreaterThan: { id: `${scope}.operatorGreaterThan`, defaultMessage: 'is greater than' },
  operatorLessThan: { id: `${scope}.operatorLessThan`, defaultMessage: 'is less than' },
  operatorContains: { id: `${scope}.operatorContains`, defaultMessage: 'contains' },
  // Create field dialog
  createFieldTitle: { id: `${scope}.createFieldTitle`, defaultMessage: 'Create a new data field' },
  createFieldNameLabel: { id: `${scope}.createFieldNameLabel`, defaultMessage: 'Field name' },
  createFieldNamePlaceholder: {
    id: `${scope}.createFieldNamePlaceholder`,
    defaultMessage: 'e.g. relocation_offered',
  },
  createFieldSubmit: { id: `${scope}.createFieldSubmit`, defaultMessage: 'Create field' },
  fieldCreated: {
    id: `${scope}.fieldCreated`,
    defaultMessage: 'Field created. You can now map a variable to it.',
  },
  recentlyCreatedCategory: {
    id: `${scope}.recentlyCreatedCategory`,
    defaultMessage: 'Recently created',
  },
  // Signatures rail (placeholder until AXHE-3716)
  signaturesComingSoon: {
    id: `${scope}.signaturesComingSoon`,
    defaultMessage: 'Signature placement is coming soon.',
  },
  // Signatures tab
  signaturesSubtitle: {
    id: `${scope}.signaturesSubtitle`,
    defaultMessage:
      'Pick a signer, then add fields onto the document. Fields you add are assigned to the highlighted signer.',
  },
  addSigner: { id: `${scope}.addSigner`, defaultMessage: 'Add signer' },
  signerNamePlaceholder: {
    id: `${scope}.signerNamePlaceholder`,
    defaultMessage: 'Signer name',
  },
  addSignerConfirm: { id: `${scope}.addSignerConfirm`, defaultMessage: 'Add' },
  removeSigner: { id: `${scope}.removeSigner`, defaultMessage: 'Remove signer' },
  addSignatureFields: {
    id: `${scope}.addSignatureFields`,
    defaultMessage: 'Add signature fields',
  },
  signatureFieldsHint: {
    id: `${scope}.signatureFieldsHint`,
    defaultMessage: 'New fields will be assigned to {name}.',
  },
  signatureFieldsNote: {
    id: `${scope}.signatureFieldsNote`,
    defaultMessage:
      'Field placement opens the document-signing editor (HelloSign) — wiring lands with the signing integration.',
  },
  uploadError: {
    id: `${scope}.uploadError`,
    defaultMessage: 'We could not upload this template. Please try again.',
  },
  // Top bar chips (Document / Applicant message) — AXHE-3814, per the
  // Tabbed Rail handoff README §3 "Settings chips bar" (TemplateSettingsChips
  // in the design project's editor.jsx is the reference markup).
  documentChipLabel: { id: `${scope}.documentChipLabel`, defaultMessage: 'Document' },
  documentChipNoFile: { id: `${scope}.documentChipNoFile`, defaultMessage: 'No document' },
  documentPopoverTitle: { id: `${scope}.documentPopoverTitle`, defaultMessage: 'Source document' },
  // Spec text says ".docx or .pdf" — narrowed to .docx-only here, matching the
  // upload modal's existing decision (constants.ts: PDF override is deferred
  // to AXHE-3720; advertising .pdf would let a drop 500 on the backend).
  documentPopoverSubtitle: {
    id: `${scope}.documentPopoverSubtitle`,
    defaultMessage:
      'The .docx we use as the canvas. Replacing keeps your variables and signature fields.',
  },
  documentCurrentFile: { id: `${scope}.documentCurrentFile`, defaultMessage: 'Current document' },
  documentReadyToReplace: {
    id: `${scope}.documentReadyToReplace`,
    defaultMessage: 'Ready to replace',
  },
  documentReplaceWarningTitle: {
    id: `${scope}.documentReplaceWarningTitle`,
    defaultMessage: 'Heads up.',
  },
  documentReplaceWarningBody: {
    id: `${scope}.documentReplaceWarningBody`,
    defaultMessage:
      'Replacing the document re-scans for placeholders. Variables that no longer appear will be flagged.',
  },
  documentPopoverDone: { id: `${scope}.documentPopoverDone`, defaultMessage: 'Done' },
  replaceDocument: { id: `${scope}.replaceDocument`, defaultMessage: 'Replace document' },
  replaceDocumentError: {
    id: `${scope}.replaceDocumentError`,
    defaultMessage: 'We could not replace this document. Please try again.',
  },
  documentReplaced: { id: `${scope}.documentReplaced`, defaultMessage: 'Document replaced.' },
  documentReplacedSummaryPrefix: {
    id: `${scope}.documentReplacedSummaryPrefix`,
    defaultMessage: 'Document replaced —',
  },
  documentReplacedFieldsAdded: {
    id: `${scope}.documentReplacedFieldsAdded`,
    defaultMessage: '{count, plural, one {1 new field detected} other {# new fields detected}}',
  },
  documentReplacedFieldsRemoved: {
    id: `${scope}.documentReplacedFieldsRemoved`,
    defaultMessage: '{count, plural, one {1 field removed} other {# fields removed}}',
  },
  applicantMessageChipEmpty: {
    id: `${scope}.applicantMessageChipEmpty`,
    defaultMessage: 'No message yet',
  },
  applicantMessagePopoverSubtitle: {
    id: `${scope}.applicantMessagePopoverSubtitle`,
    defaultMessage: 'Shown in the email and SMS that prompt the applicant to review and sign.',
  },
  applicantMessageReferenceHint: {
    id: `${scope}.applicantMessageReferenceHint`,
    defaultMessage: 'You can reference [APPLICANT_FIRST_NAME] and other variables here.',
  },
  applicantMessageCharCount: {
    id: `${scope}.applicantMessageCharCount`,
    defaultMessage: '{count} chars',
  },
  saveMessage: { id: `${scope}.saveMessage`, defaultMessage: 'Save message' },
});
